/**
 * Cliente HTTP da API. Sessão via cookie httpOnly (credentials: 'include');
 * erros viram ApiError com a mensagem em português vinda do backend.
 */
const BASE = `${(import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')}/api`;

export interface ApiFieldError {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly errors: ApiFieldError[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let unauthorizedHandler: (() => void) | null = null;
/** Chamado quando a API responde 401 (sessão expirada) fora da tela de login. */
export function onUnauthorized(handler: () => void) {
  unauthorizedHandler = handler;
}

type Query = Record<string, string | number | boolean | null | undefined>;

function buildUrl(path: string, query?: Query) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return `${BASE}${path}${qs ? `?${qs}` : ''}`;
}

/** Converte caminhos "/api/..." devolvidos pela API (fotos, logo) em URL absoluta quando necessário. */
export function assetUrl(path: string | null | undefined) {
  if (!path) return '';
  return path.startsWith('/api') ? `${BASE}${path.slice(4)}` : path;
}

async function request<T>(
  method: string,
  path: string,
  opts: { body?: unknown; query?: Query; signal?: AbortSignal } = {},
) {
  const isForm = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method,
      credentials: 'include',
      signal: opts.signal,
      headers: opts.body !== undefined && !isForm ? { 'Content-Type': 'application/json' } : undefined,
      body: opts.body === undefined ? undefined : isForm ? (opts.body as FormData) : JSON.stringify(opts.body),
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError('Não foi possível conectar ao servidor. Verifique sua conexão.', 0);
  }

  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith('/auth/login')) unauthorizedHandler?.();
    const message =
      (Array.isArray(data?.message) ? data.message[0] : data?.message) ||
      (res.status === 429 ? 'Muitas tentativas. Aguarde um minuto e tente novamente.' : `Erro ${res.status}`);
    throw new ApiError(message, res.status, data?.errors ?? []);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) => request<T>('GET', path, { query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body: body ?? {} }),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, { body }),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, { body }),
  delete: <T>(path: string, query?: Query) => request<T>('DELETE', path, { query }),
  upload: <T>(path: string, form: FormData) => request<T>('POST', path, { body: form }),
};

export function errorMessage(err: unknown, fallback = 'Algo deu errado. Tente novamente.') {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
